<template>
  <div
    v-if="visible" class="h-[85px] m-h-[85px] bg-gray-100 border-b border-gray-300 flex flex-row items-center"
    :style="{ minWidth: `${fullWidth}px` }"
  >
    <!-- Header Checkbox -->
    <div class="sticky left-0 z-10 p-3 bg-gray-100 w-9">
      &nbsp;
    </div>
    <!-- Header Cell -->
    <div
      v-for="col in visibleColumns" :key="col.property"
      class="flex flex-row items-center px-1 py-2 overflow-hidden text-sm text-left align-middle bg-gray-100"
      :style="{ width: `${col.width}px` }"
    >
      <div v-if="!utils.isDefined(indexedColumns.get(col.property)?.allowFilterAndSort)" class="w-full overflow-hidden">
        <div v-if="indexedColumns.get(col.property) && [AttributeType.Date, AttributeType.DateTime, AttributeType.TimeAgo].indexOf(indexedColumns.get(col.property)!.type) >= 0" class="flex items-center">
          <div height="52px" @click="openDateRangeFilter(col)">
            <tx-input v-model="dateRangeFilterValues[col.property]" type="text" :value="getDateRangeValue(col)" faicon="fa-light fa-calendar" />
          </div>
          <tx-button
            v-if="dateRangeFilterValues[col.property] !== ''" type="icon" faicon="fa-light fa-xmark" class="shrink-0" :title="t('dataTable.advanceFilter.clearFilter')" width="24px"
            height="24px" icon-size="15px" @click="clearFilterCriteria(col)"
          />
        </div>
        <tx-select
          v-else-if="indexedColumns.get(col.property)?.filterLookup && indexedColumns.get(col.property)!.filterLookup!.size > 0 && indexedColumns.get(col.property)?.type === AttributeType.Status"
          v-model="localFilterCriteria[col.property].statusVal" :sort-list="false"
          :data="Array.from(indexedColumns.get(col.property)?.filterLookup!, ([key, label]) => ({ key, label }))" value-prop="key" display-prop="label" filterable
          clearable @change="doChange(indexedColumns.get(col.property), isPaginated)"
        />
        <tx-select
          v-else-if="indexedColumns.get(col.property)?.filterLookup && indexedColumns.get(col.property)!.filterLookup!.size > 0 && utils.isDefined(indexedColumns.get(col.property)?.supportFilteringByMultipleValues) && indexedColumns.get(col.property)?.supportFilteringByMultipleValues === false"
          v-model="localFilterCriteria[col.property].numberVal"
          :data="Array.from(indexedColumns.get(col.property)?.filterLookup!, ([key, label]) => ({ key, label }))" value-prop="key" display-prop="label" filterable
          clearable @change="doChange(indexedColumns.get(col.property), isPaginated)"
        />
        <tx-select
          v-else-if="indexedColumns.get(col.property)?.filterLookup && indexedColumns.get(col.property)!.filterLookup!.size > 0"
          v-model="localFilterCriteria[col.property].multipleVals"
          :data="Array.from(indexedColumns.get(col.property)?.filterLookup!, ([key, label]) => ({ key, label }))" value-prop="key" display-prop="label" filterable multiple-values
          clearable @change="doChange(indexedColumns.get(col.property), isPaginated)"
        />
        <div v-else-if="indexedColumns.get(col.property)?.type === AttributeType.Int || indexedColumns.get(col.property)?.type === AttributeType.Decimal" class="flex items-center">
          <div v-if="showAdvancedFilterButton(col)" class="flex items-center">
            <tx-button
              type="confirm" :text="t('dataTable.advanceFilter.advancedFilter')" width="115px" height="30px"
              @click="openAdvanceFilter(col)"
            />
            <tx-button
              type="icon" faicon="fa-light fa-xmark" class="shrink-0" :title="t('dataTable.advanceFilter.clearFilter')" width="24px"
              height="24px" icon-size="15px" @click="clearFilterCriteria(col)"
            />
          </div>
          <div v-else class="flex items-center">
            <tx-input v-model="localFilterCriteria[col.property].numberVal" type="number" clearable @change="doChange(indexedColumns.get(col.property), isPaginated)" />
            <tx-button
              type="icon" faicon="fa-light fa-filter-list" class="shrink-0" :title="t('dataTable.advanceFilter.advancedFilter')" width="24px"
              height="24px" icon-size="15px" @click="openAdvanceFilter(col)"
            />
          </div>
        </div>
        <!-- Boolean Type Filters -->
        <tx-select
          v-else-if="[AttributeType.Bool, AttributeType.BoolStr, AttributeType.BoolInt, AttributeType.IntBool].includes(indexedColumns.get(col.property)?.type!)"
          v-model="localFilterCriteria[col.property].multipleVals"
          :data="getBooleanOptions(col)"
          value-prop="key"
          display-prop="label"
          clearable
          multiple-values
          @change="doChange(indexedColumns.get(col.property), isPaginated)"
        />
        <div v-else class="flex items-center">
          <div v-if="showAdvancedFilterButton(col)" class="flex items-center">
            <tx-button
              type="confirm" :text="t('dataTable.advanceFilter.advancedFilter')" width="115px" height="30px"
              @click="openAdvanceFilter(col)"
            />
            <tx-button
              type="icon" faicon="fa-light fa-xmark" class="shrink-0" :title="t('dataTable.advanceFilter.clearFilter')" width="24px"
              height="24px" icon-size="15px" @click="clearFilterCriteria(col)"
            />
          </div>
          <div v-else class="flex items-center">
            <tx-input v-model="localFilterCriteria[col.property].stringVal" type="text" clearable @change="doChange(indexedColumns.get(col.property), isPaginated)" />
            <tx-button
              type="icon" faicon="fa-light fa-filter-list" class="shrink-0" :title="t('dataTable.advanceFilter.advancedFilter')" width="24px"
              height="24px" icon-size="15px" @click="openAdvanceFilter(col)"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Header Actions -->
    <div
      class="w-14 h-[44px] sticky right-0 bg-gray-100 text-sm font-medium flex flex-row justify-start align-middle items-center p-2 text-center"
    >
      <tx-button type="icon" faicon="fa-light fa-xmark" @click="doCloseFilter()" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive } from 'vue'
import { cloneDeep, debounce } from 'lodash-es'
import TxSelect from '../TxSelect.vue'
import TxInput from '../TxInput.vue'
import TxButton from '../TxButton.vue'
import type { ITxDataTableColumn, ITxDataTableVisibleColumn } from './TxDataTable.types'
import { AttributeType } from '@/models/catalogAttribute'
import { FilterCriteria } from '@/models/filterCriteria'
import utils from '@/services/utils'

const props = defineProps<{
  visible: boolean
  fullWidth: number
  visibleColumns: ITxDataTableVisibleColumn[]
  indexedColumns: Map<string, ITxDataTableColumn>
  filterCriteria: Record<string, FilterCriteria>
  isPaginated?: boolean
}>()

const emits = defineEmits<{
  (e: 'update:filterCriteria', filterCriteria: Record<string, FilterCriteria>): void
  (e: 'update:visible', visible: boolean): void
  (e: 'close'): void
  (e: 'openAdvanceFilter', column: ITxDataTableVisibleColumn): void
  (e: 'openDateRangeFilter', column: ITxDataTableVisibleColumn): void
  (e: 'columnFilterChanged'): void
}>()

const { t } = useI18n()
const dateRangeFilterValues = reactive<Record<string, string>>({})
const boolStrAttributeTypeFilterLookup = [
  { key: 'true', label: t('filter.true') },
  { key: 'false', label: t('filter.false') },
]

const boolIntAttributeTypeFilterLookup = [
  { key: 1, label: t('filter.true') },
  { key: 0, label: t('filter.false') },
]
const previousFilterCriteria = reactive({ ...props.filterCriteria })

const localFilterCriteria = computed({
  get: () => props.filterCriteria,
  set: modelValue => emits('update:filterCriteria', modelValue),
})

const debouncedInputChange = debounce((column: ITxDataTableColumn) => {
  emits('update:filterCriteria', localFilterCriteria.value)
  if (JSON.stringify(previousFilterCriteria[column.property]) !== JSON.stringify(localFilterCriteria.value[column.property])) {
    previousFilterCriteria[column.property] = JSON.parse(JSON.stringify(localFilterCriteria.value[column.property]))
    emits('columnFilterChanged')
  }
}, 800)

function doChange(column?: ITxDataTableColumn, debounce = false) {
  if (!column) { return }

  const emitChange = () => {
    emits('update:filterCriteria', localFilterCriteria.value)

    if (!previousFilterCriteria[column.property]) { // avoid triggering chage event on ilter open
      previousFilterCriteria[column.property] = cloneDeep(localFilterCriteria.value[column.property])
      emits('columnFilterChanged')
      return
    }
    if (JSON.stringify(previousFilterCriteria[column.property]) !== JSON.stringify(localFilterCriteria.value[column.property])) {
      previousFilterCriteria[column.property] = JSON.parse(JSON.stringify(localFilterCriteria.value[column.property]))
      emits('columnFilterChanged')
    }
  }

  if (debounce) {
    debouncedInputChange(column)
  }
  else {
    emitChange()
  }
}

function openAdvanceFilter(col: ITxDataTableVisibleColumn) {
  emits('openAdvanceFilter', col)
}

function openDateRangeFilter(col: ITxDataTableVisibleColumn) {
  emits('openDateRangeFilter', col)
}

function getDateRangeValue(col: ITxDataTableVisibleColumn) {
  let value = ''
  if (props.filterCriteria[col.property] && props.filterCriteria[col.property].multipleVals) {
    const [start, end] = props.filterCriteria[col.property].multipleVals!
    const startDate = new Date(start)
    const endDate = new Date(end)

    if (startDate.toString() === 'Invalid Date' && endDate.toString() !== 'Invalid Date') {
      value = ` - ${endDate.toLocaleDateString()}`
    }
    else if (endDate.toString() === 'Invalid Date' && startDate.toString() !== 'Invalid Date') {
      value = `${startDate.toLocaleDateString()} - `
    }
    else if (startDate.toString() !== 'Invalid Date' && endDate.toString() !== 'Invalid Date') {
      value = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
    }
  }
  dateRangeFilterValues[col.property] = value
  return value
}

function showAdvancedFilterButton(col: ITxDataTableVisibleColumn) {
  return props.filterCriteria[col.property].isAdvancedFilter
}

function clearFilterCriteria(col: ITxDataTableVisibleColumn) {
  // clear filter criteria for the column
  localFilterCriteria.value[col.property] = new FilterCriteria({
    attribute: col.property,
    mode: FilterCriteria.attributeTypeToFilterMode(props.indexedColumns.get(col.property)!.type, props.indexedColumns.get(col.property)?.filterLookup),
    multipleVals: undefined,
    exclude: false,
    filterBlank: false,
    isAdvancedFilter: false,
  })
  emits('update:filterCriteria', localFilterCriteria.value)
  emits('columnFilterChanged')
}

function getBooleanOptions(col) {
  if (props.indexedColumns.get(col.property)?.type === AttributeType.Bool || props.indexedColumns.get(col.property)?.type === AttributeType.BoolStr) {
    return boolStrAttributeTypeFilterLookup
  }
  if (props.indexedColumns.get(col.property)?.type === AttributeType.BoolInt || props.indexedColumns.get(col.property)?.type === AttributeType.IntBool) {
    return boolIntAttributeTypeFilterLookup
  }
  return []
}

function doCloseFilter() {
  emits('update:visible', false)
  emits('close')
}
</script>
